// AI Generated Component
import { Component, OnInit } from '@angular/core';
import { StateService } from '@uirouter/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { SessionService } from '@monsido/core/session/session.service';
import { DateRangeService } from '@client/app/services/date-range/date-range.service';
import { ParamService } from '@monsido/ng2/core/param/param.service';
import { StatsRepoService } from '@client/app/services/api/stats-repo/stats-repo.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import moment from 'moment';

@Component({
    selector: 'statistics-visitors-os',
    standalone: false,
    templateUrl: './os.component.html',
})
export class StatisticsVisitorsOsComponent implements OnInit {
    loading: boolean = false;
    chartType: string = 'bar-chart';
    totalVisits: number = 0;
    summary: any = {};

    domain: any;
    date: any;
    dateFormat: any;
    exports: any[] = [];
    pages: any[] = [];
    promise: any;
    doughnutLabels: any[] = [];
    doughnutData: any[] = [];
    doughnutOptions: any = {};
    barData: any[] = [];
    barOptions: any = {};
    barOverride: any[] = [];

    constructor (
        private statsRepoService: StatsRepoService,
        private stateService: StateService,
        private paramService: ParamService,
        private translateService: TranslateService,
        private sessionService: SessionService,
        private dateRangeService: DateRangeService,
        private pipeService: PipeService,
    ) {}

    ngOnInit () {
        this.activate();
    }

    activate () {
        this.domain = this.sessionService.domain;
        this.loading = false;
        this.chartType = 'bar-chart';
        this.totalVisits = 0;
        this.summary = {};
        this.date = this.dateRangeService.setRangeDates();
        this.dateFormat = 'ddd, D. MMM YYYY';

        this.setupDoughnutSettings();
        this.setupBarChartSettings();
        this.updateDateRange();
    }

    setupBarChartSettings () {
        this.barOptions = {
            legend: {
                display: false,
            },
            scales: {
                xAxes: [
                    {
                        ticks: {
                            beginAtZero: true,
                            callback: (value: any) => {
                                return this.pipeService.get('monNumber')(value);
                            },
                            min: 0,
                        },
                    },
                ],
                yAxes: [
                    {
                        gridLines: {
                            display: false,
                        },
                    },
                ],
            },
            tooltips: {
                intersect: false,
                callbacks: {
                    title: (tooltipItem: any) => tooltipItem[0].yLabel,
                    label: (tooltipItem: any) => {
                        return this.translateService.getString('Visits') + ': ' + this.pipeService.get('monNumber')(tooltipItem.xLabel);
                    },
                },
            },
        };
        this.barOverride = [
            {
                borderWidth: 0,
                borderColor: '#50a6fb',
                backgroundColor: '#50a6fb',
            },
        ];
    }

    setupDoughnutSettings () {
        this.barData = [];
        this.doughnutLabels = [];
        this.doughnutData = [];
        this.doughnutOptions = {
            legend: {
                display: true,
                position: 'bottom',
                onHover: (element: any) => {
                    element.target.style.cursor = 'pointer';
                },
                onLeave: (element: any) => {
                    element.target.style.cursor = 'default';
                },
            },
            tooltips: {
                callbacks: {
                    title: () => {
                        return this.translateService.getString('Visits');
                    },
                    label: (tooltipItem: any, data: any) => {
                        return this.pipeService.get('monNumber')(data.datasets[0].data[tooltipItem.index]);
                    },
                },
            },
        };
    }

    getPage () {
        const params = {
            limit: 100,
            domainId: this.stateService.params.domainId,
            from: this.date.startDate.format('YYYY-MM-DD'),
            to: this.date.endDate.format('YYYY-MM-DD'),
        };
        this.exports = [
            {
                tooltip: this.translateService.getString('Export visitors operating systems'),
                name: this.translateService.getString('Visitors operating systems export'),
                data: {
                    category: 'visitors_os',
                    category_ref: this.date.startDate?.format('YYYY-MM-DD') + ',' + this.date.endDate?.format('YYYY-MM-DD'),
                },
            },
        ];
        let others = 0;
        this.loading = true;
        this.promise = this.statsRepoService.getOs(params)
            .then((data: any) => {
                this.pages = data;
                this.totalVisits = data.reduce((total: number, page: any) => {
                    return total + parseInt(page.visits);
                }, 0);
                this.doughnutLabels = [];
                this.doughnutData = [];
                for (let i = 0; i < this.pages.length; i++) {
                    if (i < 5) {
                        this.doughnutLabels.push(this.pages[i].label);
                        this.doughnutData.push(this.pages[i].visits);
                    } else {
                        others += parseInt(this.pages[i].visits);
                    }
                }
                if (this.pages.length > 5) {
                    this.doughnutLabels.push(this.translateService.getString('Others'));
                    this.doughnutData.push(others);
                }

                this.barData = [this.doughnutData];
            })
            .finally(() => {
                this.loading = false;
            });
    }

    updateDateRange () {
        if (this.date.startDate || this.date.endDate) {
            this.paramService.setParams({
                from: moment(this.date.startDate),
                to: moment(this.date.endDate),
            });
            this.getPage();
        }
    }

    getTotalBounceRate () {
        const bounceRates = this.sum(this.pages, 'bounce_rate');
        const visits = this.sum(this.pages, 'visits');
        return (bounceRates / visits) * 100;
    }

    getActionsPerVisit () {
        const actions = this.sum(this.pages, 'actions');
        const visits = this.sum(this.pages, 'visits');
        return actions / visits;
    }

    getAvgTime () {
        const avgTimes = this.sum(this.pages, 'avg_time');
        const visits = this.sum(this.pages, 'visits');
        return avgTimes / visits;
    }

    sum (collection: any, property: string) {
        if (!Array.isArray(collection)) {
            return 0;
        }

        return collection.reduce((num: number, item: any) => {
            return num + parseInt(item[property]);
        }, 0);
    }
}
